<template>
    <div class="cc-month-picker">
        <div class="cc-month-picker__header">
            <q-btn flat round dense size="sm" icon="chevron_left" :disable="viewYear <= minYear" @click="viewYear -= 1"/>
            <div class="cc-month-picker__year">{{ viewYear }}</div>
            <q-btn flat round dense size="sm" icon="chevron_right" :disable="viewYear >= maxYear" @click="viewYear += 1"/>
        </div>

        <div class="cc-month-picker__grid" @mouseleave="hoveredMonth = null">
            <button
                v-for="month in months"
                :key="month.value"
                type="button"
                class="cc-month-picker__month"
                :class="{
                    'cc-month-picker__month--edge': month.isEdge,
                    'cc-month-picker__month--within': month.isWithin,
                }"
                :disabled="month.disabled"
                @mouseenter="hoveredMonth = month.value"
                @click="pickMonth(month.value)"
            >{{ month.label }}</button>
        </div>

        <div class="cc-month-picker__hint">{{ pendingStart ? "Select the end month" : "Select the start month" }}</div>
    </div>
</template>

<script>
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Months are kept as the first day of the month, YYYY-MM-01, the only day the DATE column holds,
// so they compare as plain strings.
const monthStart = (value) => (value ? `${String(value).slice(0, 7)}-01` : null);
const yearOf = (value) => Number(String(value).slice(0, 4));

export default {
    name: "CommandCenterMonthRangePicker",

    props: {
        // { start_date, end_date } as the first days of the first and last months, or null.
        modelValue: {
            type: Object,
            default: null,
        },
        // The first and last dates in the data, as YYYY-MM-DD; months outside them cannot be picked.
        minDate: {
            type: String,
            default: null,
        },
        maxDate: {
            type: String,
            default: null,
        },
    },

    emits: ["update:modelValue"],

    data() {
        return {
            // The latest month in the data is the likeliest pick, so the picker opens on its year.
            viewYear: yearOf(this.modelValue?.end_date || this.maxDate || new Date().getFullYear()),
            // The month clicked first, while the end month is still to be picked.
            pendingStart: null,
            hoveredMonth: null,
        };
    },

    computed: {
        minMonth() {
            return monthStart(this.minDate);
        },

        maxMonth() {
            return monthStart(this.maxDate);
        },

        minYear() {
            return this.minMonth ? yearOf(this.minMonth) : -Infinity;
        },

        maxYear() {
            return this.maxMonth ? yearOf(this.maxMonth) : Infinity;
        },

        // While the end month is being picked, the range follows the month under the pointer.
        shownRange() {
            if (this.pendingStart) {
                const [start, end] = [this.pendingStart, this.hoveredMonth || this.pendingStart].sort();
                return { start, end };
            }
            if (!this.modelValue) return null;
            return { start: monthStart(this.modelValue.start_date), end: monthStart(this.modelValue.end_date) };
        },

        months() {
            const range = this.shownRange;
            return MONTH_NAMES.map((label, index) => {
                const value = `${this.viewYear}-${String(index + 1).padStart(2, "0")}-01`;
                return {
                    value,
                    label,
                    disabled: (this.minMonth && value < this.minMonth) || (this.maxMonth && value > this.maxMonth),
                    isEdge: Boolean(range) && (value === range.start || value === range.end),
                    isWithin: Boolean(range) && value > range.start && value < range.end,
                };
            });
        },
    },

    methods: {
        // The first click picks the start month and the second the end month, in either order;
        // clicking the same month twice picks that month alone.
        pickMonth(value) {
            if (!this.pendingStart) {
                this.pendingStart = value;
                return;
            }
            const [start, end] = [this.pendingStart, value].sort();
            this.pendingStart = null;
            this.$emit("update:modelValue", { start_date: start, end_date: end });
        },
    },
};
</script>

<style scoped>
.cc-month-picker {
    width: 240px;
    padding: 10px 12px 12px;
    background: #ffffff;
    font-family: Arial, Helvetica, sans-serif;
}

.cc-month-picker__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.cc-month-picker__year {
    color: #262626;
    font-size: 14px;
    font-weight: 700;
}

.cc-month-picker__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.cc-month-picker__month {
    height: 32px;
    border: 1px solid #dedede;
    border-radius: 6px;
    background: #ffffff;
    color: #262626;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
}

.cc-month-picker__month:hover:not(:disabled) {
    border-color: #a8a8a8;
}

.cc-month-picker__month:disabled {
    color: #c4c4c4;
    cursor: not-allowed;
}

.cc-month-picker__month--within {
    border-color: #ffd2bf;
    background: #fff1eb;
}

.cc-month-picker__month--edge {
    border-color: #ff4e00;
    background: #ff4e00;
    color: #ffffff;
    font-weight: 700;
}

.cc-month-picker__hint {
    margin-top: 8px;
    color: #858585;
    font-size: 11px;
    text-align: center;
}
</style>
